import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../app/hooks";
import {
  confirmPayment,
  markPaymentReceived,
} from "../../features/payments/slice-extensions/payments-slice-extension";
import { Payment } from "../../features/payments/models/Payment";
import { PaymentStatus } from "../../features/payments/models/PaymentStatus";
import { PaymentShape } from "../../features/payments/models/PaymentShape";
import { formatMoney } from "../../features/billing/utils/money";
import { paymentShapeLabel } from "../../features/payments/utils/paymentLabels";
import PaymentStatusBadge from "./PaymentStatusBadge";
import PagerControls from "./PagerControls";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";

const PAGE_SIZE = 10;

interface AttentionPaymentsTableProps {
  payments: Payment[];
  memberName: (memberId: string) => string;
  /** Called after a quick action changed a payment, so the summary can refresh. */
  onChanged: () => void;
  /** Opens the new-pack flow for the payment's member/contract. */
  onNewPack: (payment: Payment) => void;
}

/** The attention payments grid with per-row quick actions and client-side paging. */
const AttentionPaymentsTable: React.FC<AttentionPaymentsTableProps> = ({
  payments,
  memberName,
  onChanged,
  onNewPack,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil(payments.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const rows = payments.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const run = async (paymentId: string, action: Promise<unknown>) => {
    setBusyId(paymentId);
    try {
      await action;
      onChanged();
    } finally {
      setBusyId(null);
    }
  };

  const isLowPack = (p: Payment) =>
    p.shape === PaymentShape.PackPurchase &&
    p.remainingBalance !== undefined &&
    p.remainingBalance <= 2;

  return (
    <div className="mt-6 space-y-4">
      <div className="overflow-x-auto rounded-lg border border-grey-90 dark:border-primary-20">
        <table className="w-full text-sm">
          <thead className="bg-grey-95 dark:bg-primary-20 text-grey-30 dark:text-grey-90">
            <tr>
              <th className="px-4 py-2 text-left">{t("billing.table.status")}</th>
              <th className="px-4 py-2 text-left">{t("billing.table.shape")}</th>
              <th className="px-4 py-2 text-left">{t("billing.table.amount")}</th>
              <th className="px-4 py-2 text-left">{t("billing.table.duePeriod")}</th>
              <th className="px-4 py-2 text-left">{t("billing.table.member")}</th>
              <th className="px-4 py-2 text-right">{t("billing.table.quickActions")}</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-primary-10 text-grey-20 dark:text-white">
            {rows.map((p) => (
              <tr key={p.id} className="border-t border-grey-90 dark:border-primary-20">
                <td className="px-4 py-2"><PaymentStatusBadge status={p.status} /></td>
                <td className="px-4 py-2">{paymentShapeLabel(p.shape, t)}</td>
                <td className="px-4 py-2">
                  {formatMoney(p.amountMinorUnits, p.amountCurrency, getDisplayLocale())}
                  {p.remainingBalance !== undefined && (
                    <span className="ml-2 text-xs text-grey-40 dark:text-grey-80">
                      {t("billing.attentionTable.remainingShort", { count: p.remainingBalance })}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2">{p.dueDate ?? p.billingPeriodStart ?? "—"}</td>
                <td className="px-4 py-2">{memberName(p.memberId)}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {p.status === PaymentStatus.Draft && (
                    <button type="button" className="btn btn-text" disabled={busyId === p.id}
                      onClick={() => run(p.id, dispatch(confirmPayment({ id: p.id, request: {} })))}>
                      {t("billing.actions.confirm")}
                    </button>
                  )}
                  {(p.status === PaymentStatus.Invoiced || p.status === PaymentStatus.Overdue) && (
                    <button type="button" className="btn btn-text" disabled={busyId === p.id}
                      onClick={() => run(p.id, dispatch(markPaymentReceived({ id: p.id, request: {} })))}>
                      {t("billing.actions.markReceived")}
                    </button>
                  )}
                  {isLowPack(p) && (
                    <button type="button" className="btn btn-text" onClick={() => onNewPack(p)}>
                      {t("billing.actions.newPack")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PagerControls page={currentPage} totalPages={totalPages} onPageChange={setPage} />
    </div>
  );
};

export default AttentionPaymentsTable;
